<template>
    <div class="scheduling-container">
        <v-overlay :model-value="saving"
                   contained
                   class="align-center justify-center">
            <v-progress-circular color="primary"
                                 indeterminate
                                 size="64"></v-progress-circular>
        </v-overlay>
        <div class="to-schedule-list">
            <v-sheet class="booked-list"
                     border
                     color="success"
                     v-if="currentlyScheduled.length > 0">
                <div class="scheduled-header"><v-icon icon="mdi-book-check-outline"></v-icon> SCHEDULED</div>
                <small>Save these changes to schedule the following jobs.</small>
                <div v-for="element in currentlyScheduled" class="job-small-panel" :key="element.id">
                    <div class="d-flex justify-space-between">
                        <div class="job-header"><v-icon icon="mdi-progress-check"></v-icon> {{ element.name }}</div>
                        <div class="job-header">${{ numberWithCommas(element.taskSubTotal) }}</div>
                    </div>
                    <div class="d-flex justify-space-between">
                        <div class="job-header">{{ element.scheduledDate.format('ddd D/M') }}</div>
                        <div class="job-header">Line {{ element.scheduledLine }}</div>
                    </div>

                </div>
                <v-switch v-model="printCards" label="Print Job Cards?" density="compact" :hide-details="true" color="primary"></v-switch>
                <v-btn variant="outlined" prepend-icon="mdi-content-save" class="mt-1" @click="saveSchedule" size="small">
                    SCHEDULE
                </v-btn>
            </v-sheet>
            <div class="to-schedule-header">
                <v-icon icon="mdi-calendar-range"></v-icon> TO SCHEDULE
            </div>
            <div class="job-list">
                <draggable class="job-drag-list"
                           :list="jobsToSchedule"
                           group="schedule-jobs"
                           draggable=".job-panel"
                           @start="onDragStart($event)"
                           @end="onDragEnd"
                           @change="updateCard(null, null, $event)"
                           v-bind="dragOptions"
                           itemKey="id">
                    <template #item="{ element }">
                        <div class="job-panel">
                            <div class="d-flex justify-space-between">
                                <div class="job-header">{{ element.name }}</div>
                                <div class="job-header">${{ numberWithCommas(element.taskSubTotal) }}</div>
                            </div>
                            <div class="job-customer">{{ element.contact.name }}</div>
                            <div class="job-colour">{{ element.colour?.name }}</div>
                            <div v-if="element.dateReceiveEstimate || element.dateTargetCompletion" class="d-flex justify-lg-space-between">
                                <div><span v-if="element.dateReceiveEstimate"><small class="text-grey-darken-2"><v-icon icon="mdi-truck-delivery-outline"></v-icon></small> {{ $dayjs(element.dateReceiveEstimate).format("DD/MM") }}</span></div>
                                <div><span v-if="element.dateTargetCompletion"><small class="text-grey-darken-2"><v-icon icon="mdi-bullseye"></v-icon></small> {{ $dayjs(element.dateTargetCompletion).format("DD/MM") }}</span></div>
                            </div>
                        </div>
                    </template>
                </draggable>
            </div>
        </div>
        <div class="date-window">
            <v-toolbar density="compact" theme="dark">
                <v-toolbar-title>
                    <small style="color: #BDBDBD ">{{ dateHeader }}</small>
                </v-toolbar-title>
                <div class="flex-grow-1"></div>
                <v-toolbar-items>
                    <v-btn variant="outlined" @click="moveToday" size="small">
                        TODAY
                    </v-btn>
                    <v-btn variant="outlined" prepend-icon="mdi-arrow-left-circle-outline" class="ml-2" @click="previousPeriod" size="small">
                        PREVIOUS
                    </v-btn>
                    <v-btn variant="outlined" prepend-icon="mdi-arrow-right-circle-outline" class="ml-2" @click="nextPeriod" size="small">
                        NEXT
                    </v-btn>
                </v-toolbar-items>
            </v-toolbar>
            <div class="days-container">
                <v-overlay :model-value="loading"
                           contained
                           class="align-center justify-center">
                    <v-progress-circular color="primary"
                                         indeterminate
                                         size="64"></v-progress-circular>
                </v-overlay>
                <div v-for="(line, lineIndex) in ['1', '2']" class="line-block" :key="line">
                    <div class="line-header">Line {{ line }}</div>
                    <div v-if="days.length" class="calendar-block">
                        <div v-for="(day, index) in days" :key="day.date" class="calendar-day" :class="{ 'locked-day':!futureDate(day) }">
                            <div class="day-header" :class="{'today-calendar-date':day.date.isSame($dayjs(), 'day')}">{{ day.date.format('ddd D/M') }}</div>
                            <div v-if="data" class="progress-dollar" :class="{ 'over-budget': dayPercentage(index, lineIndex, day) >= 1 }">
                                <div class="progress" :style="{ 'width' : (dayPercentage(index, lineIndex, day) > 1 ? 100 : (dayPercentage(index, lineIndex, day)*100))+'%' }"></div>
                                <div class="progress-percentage">${{ numberWithCommas(dayTotal(index, lineIndex, day)/1000, 2) }} k</div>
                            </div>
                            <div v-if="data" class="day-details">
                                <div v-if="futureDate(day)" class="day-customers"><div class="day-detail-header"><v-icon icon="mdi-account" size="x-small"></v-icon> CUSTOMERS</div> <div class="day-text-small" v-for="customer in dayCustomers(index, lineIndex, day)" :key="customer" v-html="customer"></div></div>
                                <div v-if="futureDate(day)" class="day-colours"><div class="day-detail-header"><v-icon icon="mdi-palette" size="x-small"></v-icon> COLOURS</div> <div class="day-text-small" v-for="colour in dayColours(index, lineIndex, day)" :key="colour" v-html="colour"></div></div>
                            </div>
                            <div class="big-lock" v-if="!futureDate(day)">
                                <h1><v-icon icon="mdi-lock"></v-icon></h1>
                            </div>
                            <div class="job-list" v-if="futureDate(day)">
                                <draggable class="job-drag-list"
                                           :list="day.jobs[lineIndex]"
                                           group="schedule-jobs"
                                           draggable=".job-small-panel"
                                           @start="onDragStart($event)"
                                           @end="onDragEnd"
                                           @change="updateCard(line, day, $event)"
                                           v-bind="dragOptions"
                                           itemKey="id">
                                    <template #item="{ element }">
                                        <div class="job-small-panel">
                                            <div class="d-flex justify-space-between">
                                                <div class="job-header">{{ element.name }}</div>
                                                <div class="job-header">${{ numberWithCommas(element.taskSubTotal /1000, 2) }}k</div>
                                            </div>
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import { mapActions, mapState, mapGetters } from "vuex";
    import { formatSearchName } from "@/mixins/searchMixins";
    import { numberWithCommas } from "@/mixins/numberMixins";
    import draggable from 'vuedraggable'
    import _cloneDeep from "lodash/cloneDeep";
    function initialState() {
        return {
            jobsToSchedule: [],
            saving: false,
            loading: false,
            startDay: null,
            endDay: null,
            days: [],
            data: null,
            draggingJob: null,
            printCards: false,
            dragOptions: {
                animation: 200,
                group: "schedule-jobs",
                disabled: false,
                ghostClass: "job-ghost",
            }
        };
    }
    export default {
        name: "SmartBulkDateUpdater",
        components: {
            draggable,
        },
        data: function () {
            return initialState();
        },
        mounted: function () {
            this.startDay = this.$dayjs();
            this.buildDays();
            this.jobsToSchedule = _cloneDeep(this.smartScheduleJobs);
            this.currentlyScheduled = [];
        },
        props: {
        },
        methods: {
            ...mapActions([
                "showErrorSnack",
                "showSuccessSnack",
                "refreshSmartData"
            ]),
            reset: function () {
                Object.assign(this.$data, initialState());
            },
            onDragStart(event) {
                this.draggingJob = event.item.__draggable_context.element;
            },
            onDragEnd() {
                this.draggingJob = null;
            },
            buildDays() {
                let startDate = this.startDay.startOf('week').add(1, 'day');
                this.startDay = startDate.clone();
                // Get next week's Sunday
                let endDate = startDate.add(12, 'days');
                // Create an array to hold the dates
                this.days = [];
                // Loop from startDate to endDate
                while (startDate.isBefore(endDate) || startDate.isSame(endDate, 'day')) {
                    if (startDate.day() !== 0) { // 0 = Sunday
                        var calendarDay = { date: startDate.clone(), jobs: [[], []] };
                        calendarDay.jobs[0] = this.currentlyScheduled.filter(job => {
                            return job.scheduledDate.isSame(startDate) && job.scheduledLine == 1;
                        });
                        calendarDay.jobs[1] = this.currentlyScheduled.filter(job => {
                            return job.scheduledDate.isSame(startDate) && job.scheduledLine == 2;
                        });
                        this.days.push(calendarDay);
                    }
                    startDate = startDate.add(1, 'day');
                }
                this.endDay = startDate.clone().subtract(1, 'day');
                this.loadData();
            },
            moveToday() {
                this.startDay = this.$dayjs();
                this.buildDays();
            },
            previousPeriod() {
                this.startDay = this.startDay.subtract(7, 'day');
                this.buildDays();
            },
            nextPeriod() {
                this.startDay = this.startDay.add(7, 'day');
                this.buildDays();
            },
            loadData() {
                this.loading = true;
                this.$api.get('/dashboard/budgetcalendar?startDate=' + this.startDay.format('YYYY-MM-DD') + "&endDate=" + this.endDay.format('YYYY-MM-DD'))
                    .then(res => {
                        this.loading = false;
                        this.data = res.data;
                    }).catch(error => {
                        this.loading = false;
                        this.showErrorSnack("There was an issue getting the budget calendar data");
                    });
            },
            formatSearchName,
            numberWithCommas,
            resetInput() {
            },
            updateCard(lineIndex, day, event) {
                let newIndex = null;
                let job = null;
                if (event.moved && event.moved.element) {
                    newIndex = event.moved.newIndex;
                    job = event.moved.element;
                }
                if (event.added && event.added.element) {
                    newIndex = event.added.newIndex;
                    job = event.added.element;
                }
                if (job) {
                    if (day) {
                        job.scheduledDate = day.date;
                        job.scheduledLine = lineIndex;
                        // add to scheudled
                        if (!this.currentlyScheduled.includes(job)) {
                            this.currentlyScheduled.push(job);
                        }
                    } else {
                        var index = this.currentlyScheduled.indexOf(job);
                        if (index > -1) {
                            this.currentlyScheduled.splice(index, 1);
                        }
                    }
                }
            },
            dayCustomers(index, lineIndex, day) {
                // Keep track of names we've already processed
                const processedNames = new Set();
                this.data[index].lineCustomers[lineIndex].forEach(name => {
                    if (!processedNames.has(name) && this.scheduleCustomers.has(name)) {
                        if (this.draggingJob && this.draggingJob.contact.name == name) {
                            processedNames.add(`<span class='highlight-text'>${name}</span>`);
                        } else {
                            processedNames.add(name);
                        }
                    }
                });
                day.jobs[lineIndex].forEach(job => {
                    var name = job.contact.name;
                    if (this.draggingJob && this.draggingJob.contact.name == name) {
                        processedNames.add(`<span class='highlight-text'>${name}</span>`);
                    } else {
                        processedNames.add(name);
                    }
                });
                return processedNames;
            },
            dayColours(index, lineIndex, day) {
                // Keep track of colours we've already processed
                const processedColours = new Set();
                let draggingColour = this.draggingJob?.colour?.name;
                this.data[index].lineColours[lineIndex].forEach(name => {
                    // Skip if we've already processed this colour
                    if (!processedColours.has(name) && this.scheduleColours.has(name)) {
                        if (draggingColour && draggingColour === name) {
                            processedColours.add(`<span class='highlight-text'>${name}</span>`);
                        } else {
                            processedColours.add(name);
                        }
                    }
                });
                day.jobs[lineIndex].forEach(job => {
                    var name = job.colour?.name;
                    if (name) {
                        if (draggingColour && draggingColour === name) {
                            processedColours.add(`<span class='highlight-text'>${name}</span>`);
                        } else {
                            processedColours.add(name);
                        }
                    }
                });
                return processedColours;
            },
            dayPercentage(index, lineIndex, day) {
                return this.dayTotal(index, lineIndex, day) / this.data[index].lineBudgets[lineIndex]
            },
            dayTotal(index, lineIndex, day) {
                let totalValue = this.data[index].lineDollar[lineIndex];
                for (let i = 0; i < day.jobs[lineIndex].length; i++) {
                    totalValue += day.jobs[lineIndex][i].taskSubTotal;
                }
                return totalValue;
            },
            futureDate(day) {
                return day.date.isAfter(this.$dayjs(), 'day') || day.date.isSame(this.$dayjs(), 'day');
            },
            saveSchedule() {
                this.saving = true;
                this.$confirm(
                    {
                        message: `This will scheduled all ${this.currentlyScheduled.length} jobs listed. Are you sure?`,
                        disableKeys: false,
                        auth: false,
                        button: {
                            no: 'No',
                            yes: 'Yes'
                        },
                        callback: confirm => {
                            if (confirm) {
                                let formData = [];
                                let i = 0;
                                this.currentlyScheduled.forEach(job => {
                                    formData.push({
                                        id: job.id, Patch: [], ScheduleStatus: job.scheduledLine == 1 ? this.$constants.jobs.JOBSTAT_LINE1 : this.$constants.jobs.JOBSTAT_LINE2, ScheduleDate: job.scheduledDate
                                    });
                                });
                                this.$api.patch('/jobs/bulk', formData)
                                    .then(res => {
                                        // Scheduled
                                        this.saving = false;
                                        var ids = this.currentlyScheduled.map(job => job.id).join(',');
                                        this.currentlyScheduled = [];
                                        this.refreshSmartData();
                                        this.showBulkSchedule = false;
                                        if (this.printCards) {
                                            const routeData = this.$router.resolve({ name: 'jobsheetlist', query: { ids: ids } });
                                            window.open(routeData.href, '_blank');
                                        }
                                    }).catch(error => {
                                        this.showErrorSnack('There was an issue bulk updating the jobs.')
                                        this.saving = false;
                                    });
                            } else {
                                this.saving = false;
                            }
                        }
                    }
                )
            }
        },
        computed: {
            ...mapGetters([
                'smartScheduleJobs',
            ]),
            currentlyScheduled: {
                get() {
                    return this.$store.state.smart.currentlyScheduled
                },
                set(value) {
                    this.$store.commit('setCurrentlyScheduled', value)
                }
            },
            showBulkSchedule: {
                get() {
                    return this.$store.state.smart.showBulkSchedule
                },
                set(value) {
                    this.$store.commit('setShowBulkSchedule', value)
                }
            },
            dateHeader() {
                if (this.startDay) {
                    return this.startDay.format('D MMMM YYYY') + " - " + this.endDay.format('D MMMM YYYY');
                }
                return '';
            },
            scheduleCustomers() {
                // Keep track of colours we've already processed
                const processedContacts = new Set();
                this.smartScheduleJobs.forEach(job => {
                    let name = job.contact.name;
                    if (processedContacts.has(name)) return;
                    processedContacts.add(name);
                });
                return processedContacts;
            }, 
            scheduleColours() {
                const processedColours = new Set();
                this.smartScheduleJobs.forEach(job => {
                    if (job.colour?.name) processedColours.add(job.colour.name);
                });
                return processedColours;
            }
        },
        watch: {
        }
    };
</script>
<style scoped lang="scss">
    .day-details {
        font-size: 10px;
        text-wrap: auto;
    }
    .day-details .day-detail-header {
        color: #B0BEC5;
        font-size:8px;
    }
    .progress-dollar {
        border-bottom: thin solid #BDBDBD;
        padding: 0px;
        position: relative;
        background: white;
        font-size: 14px;
        height: 21px;
    }
    .progress {
        background-color: #FFF9C4;
        height: 100%;
        border-right: #FFF176 1px solid;
    }
    .over-budget .progress {
        background-color: #C8E6C9 !important;
        border-right: #43A047 1px solid !important;
    }
    .progress-percentage {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: black;
    }
    .locked-day .day-header {
        color: #B0BEC5;
    }
    .locked-day {
        background-color: #CFD8DC;
    }
    .job-drag-list {
        height: 100%;
        width: 100%;
    }
    .day-header {
        height: 20px;
        width: 100%;
        border-bottom: 1px solid #B0BEC5;
        text-align: center;
        font-size: 14px;
    }
    .job-customer {
        border-top: 1px solid #B0BEC5;
        border-bottom: 1px solid #B0BEC5;
        
    }
    .calendar-block {
        width: 100%;
        height: 100%;
        display: table;
        table-layout: fixed;
    }
    .today-calendar-date {
        background-color: #D32F2F !important;
        color:white;
    }
    .day-text-small {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .calendar-day {
        display: table-cell;
        vertical-align: top;
        box-sizing: border-box;
        border-right: 1px solid #B0BEC5;
        width: 14.2857%!important;
    }
    .days-container {
        display: flex;
        flex-direction: column;
        height: calc(100% - 80px);
    }
    .line-header {
        text-align: center;
        font-weight: bold;
        font-size: 14px;
        background-color: #CFD8DC;
        border-bottom: 1px solid #B0BEC5;
        padding-top: 5px;
        padding-bottom: 5px;
    }
    .line-block {
        height: 50%;
        width: 100%;
        border-top: 1px solid #B0BEC5;
        display: flex;
        flex-direction: inherit;
    }
    .to-schedule-header {
        width: 100%;
        text-align: center;
        background-color: #f5f7fa;
        color: #B0BEC5;
        font-size: 14px;
        font-weight: bold;
        padding-top: 5px;
        margin-bottom: 5px;
        padding-bottom: 5px;
        border-bottom: 1px solid #B0BEC5;
    }
    .big-lock {
        text-align: center;
        color: #B0BEC5;
        margin-top:40px;
    }
    .job-ghost {
        opacity: 0.2;
        border: 1px #B0BEC5 dashed !important;
    }
    .job-panel {
        background-color: #f5f7fa;
        font-size: 11px;
        padding: 5px;
        margin-bottom: 2px;
        cursor: pointer;
        white-space: pre-wrap;
        border: 1px solid #B0BEC5;
    }
    .job-small-panel {
        background-color: #f5f7fa;
        font-size: 10px;
        padding: 2px;
        margin-bottom: 2px;
        cursor: pointer;
        white-space: pre-wrap;
        border: 1px solid #B0BEC5;
    }
    .booked-list .job-small-panel {
        background-color: #C8E6C9;
        color: black;
        border: 1px solid #43A047;
    }
    .job-header {
        font-weight: bold;
    }

    .job-list {
        height: 100%;
        width: 100%;
        overflow-y: auto;
        padding-left: 2px;
        padding-right: 2px;
        padding-top:2px;
    }

    .scheduling-container {
        height: calc(100vh - 66px);
        display: flex;
        background-color: #f5f7fa;
    }

    .to-schedule-list {
        width: 12%;
        background-color: #CFD8DC;
        border: 1px solid #B0BEC5;
    }

    .date-window {
        width: 100%;
        white-space: nowrap;
        display: inline-block;
        overflow-y: auto;
        border-top: 1px solid #616161;
    }
    .booked-list {
        padding:2px;
    }
    .booked-list small {
        font-size:11px;
    }
    .scheduled-header {
        font-size: 13px;
    }
    .day-customers, .day-colours {
        max-height:200px;
        overflow-y:auto;
    }
</style>